import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, TrendingUp, TrendingDown, AlertTriangle } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  getBudgets,
  createBudget,
  updateBudget,
  deleteBudget,
  getBudgetAlerts
} from '../../../api/budgets';
import { getExpenseCategories } from '../../../api/expenseCategories';
import LoadingSpinner from '../../../components/common/LoadingSpinner';

const WholesaleBudgetsPage = () => {
  const [budgets, setBudgets] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBudget, setEditingBudget] = useState(null);
  const [formData, setFormData] = useState({
    category_id: '',
    budget_period: 'monthly',
    period_start_date: '',
    period_end_date: '',
    allocated_amount: '',
    notes: ''
  });

  useEffect(() => {
    fetchBudgets();
    fetchCategories();
    fetchAlerts();
  }, []);

  const fetchBudgets = async () => {
    try {
      setLoading(true);
      const response = await getBudgets({ is_active: true, module: 'wholesale' });
      setBudgets(response.data || []);
    } catch (error) {
      toast.error('Failed to load budgets');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await getExpenseCategories({ active_only: true });
      setCategories(response.data || []);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const fetchAlerts = async () => {
    try {
      const response = await getBudgetAlerts({ threshold: 80, module: 'wholesale' });
      setAlerts(response.data || []);
    } catch (error) {
      console.error('Failed to load alerts:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (new Date(formData.period_end_date) <= new Date(formData.period_start_date)) {
      toast.error('End date must be after start date');
      return;
    }

    try {
      const budgetData = {
        ...formData,
        allocated_amount: parseFloat(formData.allocated_amount),
        module: 'wholesale'
      };

      if (editingBudget) {
        await updateBudget(editingBudget.id, budgetData);
        toast.success('Budget updated successfully');
      } else {
        await createBudget(budgetData);
        toast.success('Budget created successfully');
      }

      setShowModal(false);
      resetForm();
      fetchBudgets();
      fetchAlerts();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save budget');
    }
  };

  const handleEdit = (budget) => {
    setEditingBudget(budget);
    setFormData({
      category_id: budget.category_id,
      budget_period: budget.budget_period,
      period_start_date: budget.period_start_date.split('T')[0],
      period_end_date: budget.period_end_date.split('T')[0],
      allocated_amount: budget.allocated_amount,
      notes: budget.notes || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id, categoryName) => {
    if (
      !window.confirm(`Are you sure you want to delete the wholesale budget for "${categoryName}"?`)
    ) {
      return;
    }

    try {
      await deleteBudget(id);
      toast.success('Budget deleted successfully');
      fetchBudgets();
      fetchAlerts();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete budget');
    }
  };

  const resetForm = () => {
    setFormData({
      category_id: '',
      budget_period: 'monthly',
      period_start_date: '',
      period_end_date: '',
      allocated_amount: '',
      notes: ''
    });
    setEditingBudget(null);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getUtilizationColor = (percentage) => {
    if (percentage >= 100) return 'bg-red-500';
    if (percentage >= 80) return 'bg-yellow-500';
    return 'bg-emerald-500';
  };

  const getAlertIcon = (alertLevel) => {
    switch (alertLevel) {
      case 'exceeded':
        return <AlertTriangle className="text-red-600" size={20} />;
      case 'warning':
        return <AlertTriangle className="text-yellow-600" size={20} />;
      default:
        return <TrendingUp className="text-emerald-600" size={20} />;
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Wholesale Budgets</h1>
            <p className="text-gray-600 mt-1">Track and manage wholesale expense budgets</p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-colors"
            style={{ backgroundColor: 'var(--wholesale-primary, #047857)' }}
          >
            <Plus size={20} />
            New Budget
          </button>
        </div>
      </div>

      {/* Alerts Section */}
      {alerts.length > 0 && (
        <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h2 className="text-lg font-semibold text-yellow-900 mb-3 flex items-center gap-2">
            <AlertTriangle size={20} />
            Budget Alerts ({alerts.length})
          </h2>
          <div className="space-y-2">
            {alerts.map((alert) => (
              <div
                key={alert.id}
                className="flex items-center justify-between bg-white p-3 rounded shadow-sm"
              >
                <div className="flex items-center gap-3">
                  {getAlertIcon(alert.alert_type)}
                  <div>
                    <span className="font-medium text-gray-900">{alert.category_name}</span>
                    <span className="text-sm text-gray-600 ml-2">
                      {alert.utilization_percentage}% utilized
                    </span>
                  </div>
                </div>
                <div className="text-sm">
                  <span className="text-gray-600">Spent:</span>
                  <span className="font-medium text-gray-900 ml-1">
                    {formatCurrency(alert.spent_amount)}
                  </span>
                  <span className="text-gray-600 mx-2">of</span>
                  <span className="font-medium text-gray-900">
                    {formatCurrency(alert.allocated_amount)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Budgets Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {budgets.length === 0 ? (
          <div className="col-span-full text-center py-12 bg-white rounded-lg shadow border border-gray-100">
            <p className="text-gray-500">No wholesale budgets found. Create one to get started.</p>
          </div>
        ) : (
          budgets.map((budget) => (
            <div
              key={budget.id}
              className="bg-white rounded-lg shadow p-6 border border-gray-100 hover:shadow-md transition-shadow"
            >
              {/* Budget Header */}
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{budget.category_name}</h3>
                  <p className="text-sm text-gray-600 capitalize">{budget.budget_period}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {formatDate(budget.period_start_date)} - {formatDate(budget.period_end_date)}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(budget)}
                    className="text-blue-600 hover:text-blue-900"
                    title="Edit"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(budget.id, budget.category_name)}
                    className="text-red-600 hover:text-red-900"
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {/* Budget Amounts */}
              <div className="space-y-3 mb-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Allocated:</span>
                  <span className="font-semibold text-gray-900">
                    {formatCurrency(budget.allocated_amount)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Spent:</span>
                  <span className="font-semibold text-gray-900">
                    {formatCurrency(budget.spent_amount)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Remaining:</span>
                  <span
                    className={`font-semibold ${budget.remaining_amount >= 0 ? 'text-emerald-600' : 'text-red-600'}`}
                  >
                    {formatCurrency(budget.remaining_amount)}
                  </span>
                </div>
              </div>

              {/* Utilization Bar */}
              <div className="mb-3">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm text-gray-600">Utilization</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {budget.utilization_percentage}%
                  </span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${getUtilizationColor(budget.utilization_percentage)}`}
                    style={{ width: `${Math.min(budget.utilization_percentage, 100)}%` }}
                  />
                </div>
              </div>

              {/* Alert Level */}
              <div className="flex items-center gap-2">
                {budget.alert_level === 'exceeded' ? (
                  <span className="flex items-center gap-1 text-xs text-red-600 font-medium">
                    <TrendingDown size={12} />
                    Over Budget
                  </span>
                ) : budget.alert_level === 'warning' ? (
                  <span className="flex items-center gap-1 text-xs text-yellow-600 font-medium">
                    <AlertTriangle size={12} />
                    Approaching Limit
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-xs text-emerald-600 font-medium">
                    <TrendingUp size={12} />
                    On Track
                  </span>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                {editingBudget ? 'Edit Wholesale Budget' : 'Create New Wholesale Budget'}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                <select
                  value={formData.category_id}
                  onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  required
                  disabled={editingBudget}
                >
                  <option value="">Select Category</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Budget Period *
                </label>
                <select
                  value={formData.budget_period}
                  onChange={(e) => setFormData({ ...formData, budget_period: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  required
                >
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    value={formData.period_start_date}
                    onChange={(e) =>
                      setFormData({ ...formData, period_start_date: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date *</label>
                  <input
                    type="date"
                    value={formData.period_end_date}
                    onChange={(e) => setFormData({ ...formData, period_end_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Allocated Amount (₦) *
                </label>
                <input
                  type="number"
                  value={formData.allocated_amount}
                  onChange={(e) => setFormData({ ...formData, allocated_amount: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="0.00"
                  step="0.01"
                  min="0.01"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  rows="3"
                  placeholder="Additional notes or justification for this wholesale budget"
                />
              </div>

              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  type="submit"
                  className="flex-1 text-white px-4 py-2 rounded-lg transition-colors"
                  style={{ backgroundColor: 'var(--wholesale-primary, #047857)' }}
                >
                  {editingBudget ? 'Update' : 'Create'} Budget
                </button>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default WholesaleBudgetsPage;
